import classNames from 'classnames'
import { Icon, Button, Card } from '../../../atoms'
import React from 'react'
import { connect } from 'react-redux'
import { FsCore } from 'types/fs-core'
import Image from '../../imgix-img'
import { formatThousands } from '../../../utils/formatter'
import dayjs from '../../../utils/date'
import { CircularPercentageBar } from '../../product-table-v2/cell'

const iwocaInfo = {
  copy: {
    offers_available: {
      title: "You're eligible for a loan with iwoca.",
      body: `<div>Thank you for filling in your details. You can now explore the loan offers and select the one that's suitable for your business. All the information you've given us will transfer to iwoca.<br/>  <br/>If you have any questions, please contact iwoca directly on 02037780274 . If you call them on the phone number used in the application this will help you reach the right person</div>`,
    },
    deferred: {
      title: 'We’ve sent your application to iwoca',
      body: `<div>iwoca will be in touch shortly to discuss the outcome of your application, so keep an eye on your inbox and have your phone to hand.<br/>  <br/>If you have any questions, please contact iwoca directly on 02037780274 . If you call them on the phone number used in the application this will help you reach the right person</div>`,
    },
    other: {
      title: 'We’ve sent your application to iwoca',
      body: `<div>iwoca will be in touch shortly to discuss the outcome of your application, so keep an eye on your inbox and have your phone to hand.<br/>  <br/>If you have any questions, please contact iwoca directly on 02037780274 . If you call them on the phone number used in the application this will help you reach the right person</div>`,
    },
    decline: {
      title: 'Unfortunately, your business is not eligible for a loan.',
      body: `<div>Thank you for filling in your details but we couldn't find a loan that's right for your business.<br/> <br/>To improve your chances of getting a loan, find out what's impacting your business credit score. You can also explore our other products below.</div>`,
    },
    declined: {
      title: 'Unfortunately, your business is not eligible for a loan.',
      body: `<div>Thank you for filling in your details but we couldn't find a loan that's right for your business. This could be because your credit score is too low or your business might also fall below iwoca’s minimum approval score. <br/> <br/>To improve your chances of getting a loan, take a look at your business credit score and find out what's impacting it. You can also explore our alternative products below.</div>`,
    },
  },
}

interface IwocaResultsProps {
  iwocaResult: {
    offers?: {
      duration: {
        unit: string
        amount: number
      }
      max_amount: number

      is_suggested: boolean

      offer_id: string

      personal_guarantee_proportion?: number

      payment_interval?: '30d' | '14d' | '7d' | '1m' | '1d'

      interest_only_repayments?: number
      offer_type: 'confirmed' | 'indicative' | 'sample'
      approximate_apr?: number

      interest_rate?: number

      fee_percentage?: number

      probability_of_offer?: 'very_high' | 'high' | 'medium' | 'low' | 'very_low'

      timeout: string
    }[]
    status: 'offers_available' | 'deferred' | 'other' | 'decline' | 'declined'
    link: string
  }
}

const IwocaResults: React.FC<IwocaResultsProps> = ({ iwocaResult }) => {
  const bestConfirmedOffer = iwocaResult.offers?.filter(
    (offer) => offer.offer_type === 'confirmed' && offer.is_suggested === true,
  )[0]
  const indicativeOffer = iwocaResult.offers?.filter(
    (offer) => offer.offer_type === 'indicative' && offer.is_suggested === true,
  )[0]
  const sampleOffer = iwocaResult.offers?.filter(
    (offer) => offer.offer_type === 'sample' && offer.is_suggested === true,
  )[0]
  const paymentInterval = {
    '30d': 'Monthly',
    '14d': 'Fortnightly',
    '7d': 'Weekly',
    '1m': 'Monthly',
    '1d': 'Daily',
  }
  return (
    <div
      className={classNames('ResultsCard', {
        'ResultsCard--iwocaDecline': iwocaResult.status === 'decline',
      })}
    >
      <div className='ResultsCard__title ResultsCard__title--iwoca'>
        {iwocaResult.status === 'offers_available' && (
          <Icon glyph='tick-circle-bg' size={48} desktopSize={64} fill='#924a8b' />
        )}
        <h2>
          {indicativeOffer
            ? `You could be eligible for a loan up to £${formatThousands(
                indicativeOffer.max_amount,
              )} with iwoca.`
            : sampleOffer
            ? 'You could be eligible for a loan with iwoca.'
            : bestConfirmedOffer
            ? 'You’ve been approved for a loan with iwoca.'
            : iwocaInfo.copy[iwocaResult.status]?.title}
        </h2>
      </div>
      <div className='ResultsCard__details ResultsCard__details--iwoca'>
        <div
          className={classNames('ResultsCard__details__body--iwoca', {
            'ResultsCard__details__body--iwoca--withOffer': bestConfirmedOffer,
          })}
        >
          <div dangerouslySetInnerHTML={{ __html: iwocaInfo.copy[iwocaResult.status]?.body }} />
          {bestConfirmedOffer ? (
            <>
              <div className='ResultsCard__details__offer'>
                You&apos;re approved for{' '}
                <strong>£{formatThousands(bestConfirmedOffer.max_amount)}</strong> valid until{' '}
                <strong>{dayjs(bestConfirmedOffer.timeout).format('DD MMMM YYYY')}</strong>
              </div>
              <div className='ResultsCard__details__offer__wrapper'>
                <div className='ResultsCard__details__offer__card'>
                  <div className='ResultsCard__details__offer__card--badge'>🚀 Approved offer</div>
                  <Card variant='modal'>
                    <div className='ResultsCard__details__offer__card--term'>
                      <span>Term Length</span>
                      <span>
                        {bestConfirmedOffer.duration.amount} {bestConfirmedOffer.duration.unit}
                      </span>

                      <div>
                        <CircularPercentageBar percent={'100'} color={'#00892B'} />
                      </div>
                    </div>
                    <div className='ResultsCard__details__offer__card--info'>
                      <div className='ResultsCard__details__offer__card--info--item'>
                        <strong>Upfront fee</strong>
                        <span>{bestConfirmedOffer.fee_percentage ?? 0}%</span>
                      </div>

                      {bestConfirmedOffer.interest_rate !== undefined && (
                        <div className='ResultsCard__details__offer__card--info--item'>
                          <strong>Interest rate</strong>
                          <span>{bestConfirmedOffer.interest_rate}%</span>
                        </div>
                      )}
                      {bestConfirmedOffer.payment_interval && (
                        <div className='ResultsCard__details__offer__card--info--item'>
                          <strong>Payment term</strong>
                          <span>{paymentInterval[bestConfirmedOffer.payment_interval]}</span>
                        </div>
                      )}
                      <Image
                        src={
                          'https://img.money.co.uk/s3/uswitch-assets-eu/amp/images/provider/logos/iwoca_2021.png'
                        }
                        width={160}
                        critical={false}
                        alt={'iwoca Logo'}
                      />
                    </div>
                    {/* <div className='ResultsCard__details__offer__card--image'>
                  <Image
                    src={
                      'https://img.money.co.uk/s3/uswitch-assets-eu/amp/images/provider/logos/iwoca_2021.png'
                    }
                    width={160}
                    critical={false}
                    alt={'iwoca Logo'}
                  />
                </div> */}
                  </Card>
                </div>
                <div className='ResultsCard__details__offer__usps'>
                  <div className='ResultsCard__details__offer__usps--item'>
                    <Icon glyph='card-rates' size={44} />
                    <span>Monthly interest only charged on loan amount outstanding</span>
                  </div>
                  <div className='ResultsCard__details__offer__usps--item'>
                    <Icon glyph='exclusive-deal' size={32} color='#34454E' fill='#34454E' />
                    <span>No early repayment fees</span>
                  </div>
                  <div className='ResultsCard__details__offer__usps--item'>
                    <Icon glyph='business-loans-dark' size={36} color='#34454E' fill='#34454E' />
                    <span>Borrow as much or as little of your credit limit as you choose</span>
                  </div>
                </div>
              </div>
            </>
          ) : iwocaResult.status !== 'decline' && iwocaResult.status !== 'declined' ? (
            <div className='ResultsCard__details__offer__usps'>
              <div className='ResultsCard__details__offer__usps--item'>
                <Icon glyph='card-rates' size={44} />
                <span>Monthly interest only charged on loan amount outstanding</span>
              </div>
              <div className='ResultsCard__details__offer__usps--item'>
                <Icon glyph='exclusive-deal' size={32} color='#34454E' fill='#34454E' />
                <span>No early repayment fees</span>
              </div>
              <div className='ResultsCard__details__offer__usps--item'>
                <Icon glyph='business-loans-dark' size={36} color='#34454E' fill='#34454E' />
                <span>Borrow as much or as little of your credit limit as you choose</span>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
      <div className='ResultsCard__details__footer'>
        {iwocaResult.status === 'offers_available' && iwocaResult.link && (
          <div className='ResultsCard__cta'>
            <Button variant='primary' size='md' href={iwocaResult.link} openInNewTab>
              View loan offers
            </Button>
          </div>
        )}
        {!bestConfirmedOffer && iwocaResult.status !== 'decline' && (
          <Image
            src={
              'https://img.money.co.uk/s3/uswitch-assets-eu/amp/images/provider/logos/iwoca_2021.png'
            }
            width={180}
            height={66}
            critical={false}
            alt={'iwoca Logo'}
          />
        )}
      </div>
    </div>
  )
}

export default connect((state: FsCore.State, props: any) => {
  return {
    ...props,
    iwocaResult: state.components.iwocaResult,
  }
})(IwocaResults)
